import React, { useMemo, useRef, useState } from 'react';
import { CopyTextButton } from './CopyTextButton';
import { clearStoredPhotoIndex, loadedPhotoIndex, parsePhotoIndex, storePhotoIndex } from '../../data/guide/monsterPhotoIndexNotes';
import { contrastPhotoIndex, formatContrast } from '../../utils/photoIndexContrast';
import type { WorldData } from '../../types/world';

/**
 * Carga del índice original (VANGUARD_MONSTRUOS_INDICE_COMPACTO.json) desde el navegador del staff.
 * Se guarda en este navegador; los visitantes solo lo verán cuando el JSON esté en el proyecto.
 */
export function PhotoIndexLoader({
  w,
  onChange
}: {
  w: WorldData;
  onChange: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string>();
  const [version, setVersion] = useState(0);
  const index = useMemo(() => loadedPhotoIndex(), [version]);
  const contrast = useMemo(() => index ? contrastPhotoIndex(w, Array.from(index.values())) : undefined, [w, index]);
  const accept = (text: string) => {
    const {
      records,
      error: e
    } = parsePhotoIndex(text);
    if (e || !records.length) {
      setError(e ?? 'El índice no contiene registros.');
      return;
    }
    storePhotoIndex(text);
    setError(undefined);
    setVersion(v => v + 1);
    onChange();
  };
  const onFile = async (f?: File) => {
    if (f) accept(await f.text());
  };
  return <div className="mt-4 rounded-2xl border border-white/10 p-4" data-id="element-2247" data-mp="1gu6jmynjyjay">
      <h4 className="text-sm font-semibold text-white" data-id="element-2248" data-mp="1lqeqzpvid87k">Índice original de fotos</h4>
      <p className="mt-1 max-w-3xl text-xs text-gray-400" data-id="element-2249" data-mp="1ps09pruq4gpo">
        Carga VANGUARD_MONSTRUOS_INDICE_COMPACTO.json para contrastar cada ID con la lámina y el recorte ya cargados. Se guarda solo en este navegador:
        para que lo vean los visitantes, adjúntalo en el chat del proyecto.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2" data-id="element-2250" data-mp="en6k5yckyq5">
        <input ref={fileRef} type="file" accept=".json,application/json" className="sr-only" onChange={e => onFile(e.target.files?.[0])} aria-label="Elegir el archivo JSON del índice" data-id="element-2251" data-mp="5cwzsb0ihp94" />
        <button type="button" onClick={() => fileRef.current?.click()} className="h-9 rounded-xl border border-white/10 px-3 text-sm font-semibold text-white transition-colors duration-150 hover:border-[#D4AF37]/40" data-id="element-2252" data-mp="3tjg52rwt3oei">
          {index ? 'Reemplazar índice' : 'Cargar índice JSON'}
        </button>
        {index && <button type="button" onClick={() => {
        clearStoredPhotoIndex();
        setVersion(v => v + 1);
        onChange();
      }} className="h-9 rounded-xl px-3 text-sm text-gray-400 transition-colors duration-150 hover:text-white" data-id="element-2253" data-mp="gauxblzuc8ge">
            Quitar índice
          </button>}
        {contrast && <CopyTextButton text={formatContrast(contrast)} label="Copiar contraste del índice" data-id="element-2254" data-mp="l45wd0yevkxr" />}
      </div>
      {error && <p role="alert" className="mt-2 text-sm text-red-300" data-id="element-2255" data-mp="v0izsqjrdd5p">
          {error}
        </p>}
      {!index && <details className="mt-3" data-id="element-2256" data-mp="1emu9su2gesh7">
          <summary className="cursor-pointer text-xs text-gray-400" data-id="element-2257" data-mp="1ejokbdpwxoky">O pega aquí el contenido del JSON</summary>
          <textarea aria-label="Contenido del JSON del índice" className="mt-2 h-28 w-full rounded-xl border border-white/10 bg-white/[0.03] p-2 font-mono text-xs text-gray-200" onBlur={e => e.target.value.trim() && accept(e.target.value)} placeholder='{"monsters": {"0": {"sheet": "...", "rect": [x, y, w, h]}}}' data-id="element-2258" data-mp="1il1s3tckg1ui" />
        </details>}
      {contrast && <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4" data-id="element-2259" data-mp="lxdm29ps3zrb">
          {[{
        label: 'IDs en el índice',
        value: contrast.total,
        note: 'referencia 285'
      }, {
        label: 'Contrastados',
        value: contrast.matched.length,
        note: 'ID + lámina + recorte'
      }, {
        label: 'Referencias de familia',
        value: contrast.family.length,
        note: 'referencia 101'
      }, {
        label: 'Discrepancias',
        value: contrast.discrepancies.length,
        note: `${contrast.missingFromIndex.length} IDs de las láminas fuera del índice`
      }].map(c => <div key={c.label} className="rounded-xl bg-white/[0.03] px-4 py-3" data-id="element-2260" data-mp="1fc7lgbaxuomx">
              <dt className="text-xs text-gray-400" data-id="element-2261" data-mp="1ahagbfgs4zfb">{c.label}</dt>
              <dd className="mt-0.5 font-mono text-xl font-semibold text-white" data-id="element-2262" data-mp="15j0j6zbhvor2">{c.value}</dd>
              <dd className="text-xs text-gray-500" data-id="element-2263" data-mp="10l7bu2yggop7">{c.note}</dd>
            </div>)}
        </dl>}
    </div>;
}